import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api, { fmtDate, apiError } from "@/lib/api";
import { Modal, Field, Select } from "@/pages/Notizie";
import { toast } from "sonner";
import { LongTextarea, NoteView } from "@/components/LongText";
import {
  Phone, StickyNote, MessageCircle, Mail, CalendarClock, Eye, FileText, FileSignature,
  RefreshCw, Settings, Clock, Plus, X, Search, Users2,
} from "lucide-react";

const TIPO = {
  chiamata: { icon: Phone, label: "Chiamata", cls: "text-emerald-500 bg-emerald-500/10", cat: "chiamata" },
  nota: { icon: StickyNote, label: "Nota", cls: "text-[#FFB020] bg-[#FFB020]/10", cat: "nota" },
  whatsapp: { icon: MessageCircle, label: "WhatsApp", cls: "text-[#25D366] bg-[#25D366]/10", cat: "whatsapp" },
  email: { icon: Mail, label: "Email", cls: "text-[#38BDF8] bg-[#38BDF8]/10", cat: "email" },
  incontro: { icon: Users2, label: "Incontro", cls: "text-[#A78BFA] bg-[#A78BFA]/10", cat: "incontro" },
  altro: { icon: StickyNote, label: "Altro", cls: "text-slate-500 bg-slate-500/10", cat: "altro" },
  appuntamento: { icon: CalendarClock, label: "Appuntamento", cls: "text-[#FC82ED] bg-[#FC82ED]/10", cat: "appuntamento" },
  visita: { icon: Eye, label: "Visita", cls: "text-[#3B82F6] bg-[#3B82F6]/10", cat: "visita" },
  feedback: { icon: StickyNote, label: "Feedback", cls: "text-[#3B82F6] bg-[#3B82F6]/10", cat: "visita" },
  proposta: { icon: FileText, label: "Proposta", cls: "text-[#EF4444] bg-[#EF4444]/10", cat: "proposta" },
  preliminare: { icon: FileSignature, label: "Preliminare", cls: "text-[#EF4444] bg-[#EF4444]/10", cat: "proposta" },
  rogito: { icon: FileSignature, label: "Rogito", cls: "text-[#EF4444] bg-[#EF4444]/10", cat: "proposta" },
  incarico_acquisito: { icon: RefreshCw, label: "Incarico", cls: "text-emerald-500 bg-emerald-500/10", cat: "sistema" },
  sistema: { icon: Settings, label: "Sistema", cls: "text-slate-400 bg-slate-400/10", cat: "sistema" },
};
const meta = (t) => TIPO[t] || TIPO.sistema;
const FILTERS = [["chiamata", "Chiamate"], ["whatsapp", "WhatsApp"], ["email", "Email"], ["appuntamento", "Appuntamenti"],
  ["visita", "Visite"], ["proposta", "Proposte"], ["nota", "Note"], ["sistema", "Sistema"]];
const ESITI = [["risposto", "Risposto"], ["non_risposto", "Non risposto"], ["da_richiamare", "Da richiamare"]];

export default function ComunicazioniModal({ entityType, entityId, name, phone, onClose }) {
  const [events, setEvents] = useState(null);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ tipo: "chiamata", testo: "", esito: "", data: "" });
  const [saving, setSaving] = useState(false);
  const [sel, setSel] = useState(FILTERS.map(([k]) => k));
  const [q, setQ] = useState("");
  const [esitoFor, setEsitoFor] = useState(null);
  const [esitoNota, setEsitoNota] = useState("");
  const pending = useRef(null);
  const isClient = entityType === "proprietario" || entityType === "acquirente";

  const load = () => {
    const url = isClient ? `/clienti/timeline?entity_type=${entityType}&entity_id=${entityId}`
      : `/comunicazioni?entity_type=${entityType}&entity_id=${entityId}`;
    api.get(url).then((r) => {
      const data = r.data;
      setEvents(isClient ? (data.events || []) : (data || []).map((c) => ({ ...c, autore: c.user_name, source: "comunicazione" })));
    }).catch((e) => { toast.error(apiError(e)); setEvents([]); });
  };
  useEffect(() => { load(); }, [entityId]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && pending.current) {
        const t = pending.current; pending.current = null; setEsitoFor(t); setEsitoNota("");
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const register = async (payload) => {
    await api.post("/comunicazioni", { entity_type: entityType, entity_id: entityId, ...payload });
  };

  const save = async () => {
    if (!f.testo.trim() && !f.esito) return toast.error("Inserisci un esito o una nota");
    setSaving(true);
    try {
      await register({ tipo: f.tipo, testo: f.testo, esito: f.esito || null, data: f.data ? new Date(f.data).toISOString() : null });
      toast.success("Comunicazione registrata");
      setF({ tipo: "chiamata", testo: "", esito: "", data: "" }); setAdding(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const startAction = (tipo) => {
    pending.current = tipo;
    if (tipo === "chiamata") window.location.href = `tel:${phone}`;
    else window.open(`https://wa.me/${String(phone || "").replace(/\D/g, "")}`, "_blank");
  };

  const submitEsito = async (esito) => {
    try {
      const label = esito === "risposto" ? "Risposto" : esito === "non_risposto" ? "Non risposto" : "Da richiamare";
      await register({ tipo: esitoFor, esito, testo: `${label}${esitoNota ? " · " + esitoNota : ""}` });
      if (esito === "da_richiamare") {
        try { await api.post("/attivita", { titolo: `Richiamare ${name}`, descrizione: esitoNota || "" }); } catch { /* task opzionale */ }
      }
      toast.success("Esito registrato");
      setEsitoFor(null); setEsitoNota(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const filtered = useMemo(() => (events || []).filter((e) => {
    if (!sel.includes(meta(e.tipo).cat)) return false;
    if (q && !`${e.testo} ${TIPO[e.tipo]?.label || ""} ${e.autore || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [events, sel, q]);

  const toggle = (k) => setSel((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="comunicazioni-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] flex flex-col card-immo overflow-hidden">
        <div className="px-6 pt-6 pb-3 border-b border-[var(--border)] shrink-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-display text-lg font-bold">Timeline cliente</h3>
            <button onClick={onClose} data-testid="comm-close" className="p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button>
          </div>
          <p className="text-sm text-[var(--muted)]">{name}</p>
          {isClient && phone && (
            <div className="flex gap-2 mt-3">
              <button data-testid="comm-call-btn" onClick={() => startAction("chiamata")} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-emerald-500 text-white text-sm font-semibold"><Phone size={14} /> Chiama</button>
              <button data-testid="comm-wa-btn" onClick={() => startAction("whatsapp")} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#25D366] text-white text-sm font-semibold"><MessageCircle size={14} /> WhatsApp</button>
            </div>
          )}
        </div>

        <div className="px-6 py-4 flex-1 overflow-y-auto overscroll-contain">
          {!adding ? (
            <button data-testid="comm-add-btn" onClick={() => setAdding(true)}
                    className="w-full mb-4 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm glow-pink">
              <Plus size={15} /> Registra comunicazione
            </button>
          ) : (
            <div className="mb-4 p-4 rounded-xl border border-[var(--border)] space-y-3">
              <Select label="Tipo" value={f.tipo} testid="comm-tipo" onChange={(e) => setF({ ...f, tipo: e.target.value })}
                      opts={[["chiamata", "Chiamata"], ["whatsapp", "WhatsApp"], ["email", "Email"], ["incontro", "Incontro"], ["altro", "Altro"]]} />
              <Field label="Data e ora" type="datetime-local" testid="comm-data" value={f.data} onChange={(e) => setF({ ...f, data: e.target.value })} />
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Esito</label>
                <LongTextarea data-testid="comm-esito" value={f.esito} onChange={(e) => setF({ ...f, esito: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1.5">Nota</label>
                <LongTextarea data-testid="comm-testo" value={f.testo} onChange={(e) => setF({ ...f, testo: e.target.value })} rows={3}
                          className="w-full px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] focus:border-[#FC82ED] outline-none text-sm resize-none" />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setAdding(false)} className="flex-1 py-2 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-sm">Annulla</button>
                <button onClick={save} disabled={saving} data-testid="comm-save" className="flex-1 py-2 rounded-lg bg-[#FC82ED] text-black font-semibold text-sm disabled:opacity-60">Salva</button>
              </div>
            </div>
          )}

          {/* Filters + search */}
          <div className="mb-3 space-y-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="comm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nel testo…"
                     className="w-full pl-8 pr-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
            </div>
            <div className="flex flex-wrap gap-1.5">
              {FILTERS.map(([k, l]) => (
                <button key={k} data-testid={`comm-filter-${k}`} onClick={() => toggle(k)}
                        className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${sel.includes(k) ? "bg-[#111827] text-white border-[#111827]" : "border-[var(--border)] text-slate-400"}`}>{l}</button>
              ))}
            </div>
          </div>

          {esitoFor && (
            <div className="mb-4 p-3 rounded-xl border border-emerald-200 bg-emerald-50" data-testid="comm-esito-prompt">
              <p className="text-sm font-semibold mb-2">Esito {esitoFor === "chiamata" ? "chiamata" : "WhatsApp"}?</p>
              <LongTextarea data-testid="comm-esito-nota" value={esitoNota} onChange={(e) => setEsitoNota(e.target.value)} placeholder="Nota (facoltativa)" className="w-full mb-2 px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
              <div className="flex gap-2">
                {ESITI.map(([v, l]) => (
                  <button key={v} data-testid={`comm-esito-${v}`} onClick={() => submitEsito(v)} className="flex-1 py-2 rounded-lg bg-white border border-[var(--border)] text-xs font-semibold hover:bg-slate-50">{l}</button>
                ))}
              </div>
            </div>
          )}

          {events === null ? <div className="skeleton h-32 rounded-xl" /> :
            filtered.length === 0 ? <p className="text-sm text-[var(--muted)] text-center py-6">Nessun evento.</p> :
            <div className="relative pl-6">
              <div className="absolute left-[9px] top-1 bottom-1 w-px bg-[var(--border)]" />
              <div className="space-y-4">
                {filtered.map((c, i) => {
                  const t = meta(c.tipo); const Icon = t.icon;
                  return (
                    <div key={i} className="relative" data-testid={`comm-item-${i}`}>
                      <div className={`absolute -left-6 w-[19px] h-[19px] rounded-full flex items-center justify-center ${t.cls}`}><Icon size={11} /></div>
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="text-xs font-semibold">{t.label}</span>
                        {c.esito && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{c.esito}</span>}
                        <span className="text-[11px] text-[var(--muted)] flex items-center gap-1"><Clock size={10} /> {fmtDate(c.data)} · {c.data ? new Date(c.data).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : ""}</span>
                      </div>
                      {c.testo && <NoteView text={c.testo} className="text-sm text-slate-600" />}
                      {c.autore && <p className="text-[11px] text-[var(--muted)] mt-0.5">— {c.autore}</p>}
                    </div>
                  );
                })}
              </div>
            </div>}
        </div>
      </div>
    </div>,
    document.body
  );
}
